/**
 * Price formatting utilities for HUL Proposal Website
 */

const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const USD_FORMATTER_NO_CENTS = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const NUMBER_FORMATTER = new Intl.NumberFormat('en-US');

/**
 * Format a number as USD currency
 */
export function formatCurrency(amount: number, showCents = true): string {
  const formatter = showCents ? USD_FORMATTER : USD_FORMATTER_NO_CENTS;
  return formatter.format(amount);
}

/**
 * Format a number as USD with appropriate sign for credits
 */
export function formatCurrencyWithSign(
  amount: number,
  isCredit: boolean,
  showCents = true
): string {
  const formatted = formatCurrency(Math.abs(amount), showCents);
  return isCredit ? `-${formatted}` : formatted;
}

/**
 * Format a percentage value
 */
export function formatPercentage(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format a number with commas
 */
export function formatNumber(value: number): string {
  return NUMBER_FORMATTER.format(value);
}

/**
 * Format monthly payment display
 */
export function formatMonthlyPayment(amount: number, term: number): string {
  return `${formatCurrency(amount)}/mo × ${term} months`;
}

/**
 * Parse a currency string back to a number
 */
export function parseCurrency(value: string): number {
  const cleaned = value.replace(/[^0-9.-]/g, '');
  return parseFloat(cleaned) || 0;
}

/**
 * Calculate and format savings percentage
 */
export function formatSavings(original: number, discounted: number): string {
  if (original <= 0) return '0%';
  const savings = ((original - discounted) / original) * 100;
  return `Save ${savings.toFixed(0)}%`;
}

/**
 * Format capacity with unit
 */
export function formatCapacity(capacity: string): string {
  // If already has "lbs" or similar, return as-is
  if (/lbs|lb|kg/i.test(capacity)) {
    return capacity;
  }
  // Assume lbs if just a number
  const num = parseInt(capacity.replace(/,/g, ''), 10);
  if (!isNaN(num)) {
    return `${formatNumber(num)} lbs`;
  }
  return capacity;
}

/**
 * Format hours with commas
 */
export function formatHours(hours: number): string {
  return `${formatNumber(hours)} hrs`;
}
